import { useEffect, useState } from "react";

import { api } from "../../api.js";
import { formatEventTimeLocal } from "../../lib/dashboardTime.js";
import { useReportingTimezone } from "../../lib/ReportingTimezoneContext.js";
import type { FilterState, WifiSignalSeries } from "../../types.js";

/** Fixed chart scales so threshold lines sit in the same place for every table. */
const RSSI_MIN = -100;
const RSSI_MAX = 0;
const RSSI_BAD_BELOW = -80;
const TX_SCALE_MAX = 100;
const TX_BAD_BELOW = 5;

/** Distinct series colours (not table/venue palette — one table at a time). */
const RSSI_COLOR = "#4f80c7";
const TX_COLOR = "#c77b4f";
const FILL_OPACITY = 0.28;
const LINE_WIDTH = 2.25;

const CHART_H = 160;
const PAD = { top: 14, right: 56, bottom: 28, left: 52 };
const WIDTH = 740;

/** Left axis (RSSI dBm): top is 0, bottom is −100. */
const RSSI_TICKS = [0, -20, -40, -60, -80, -100] as const;
/** Right axis (TX Mbps): top is 100, bottom is 0. */
const TX_TICKS = [100, 80, 60, 40, 20, 0] as const;

function ThresholdLine({
  y,
  label,
  labelSide,
  color,
}: {
  y: number;
  label: string;
  labelSide: "left" | "right";
  color: string;
}) {
  return (
    <g>
      <line
        x1={PAD.left}
        y1={y}
        x2={PAD.left + (WIDTH - PAD.left - PAD.right)}
        y2={y}
        stroke={color}
        strokeWidth={1.5}
        strokeDasharray="5 3"
        opacity={0.95}
      />
      <text
        x={labelSide === "left" ? PAD.left + 4 : WIDTH - PAD.right - 4}
        y={y - 3}
        textAnchor={labelSide === "left" ? "start" : "end"}
        fill={color}
        className="text-[8px] font-semibold"
      >
        {label}
      </text>
    </g>
  );
}

/** Build stroke path + closed area path (line → floor → back). */
function seriesPaths(
  samples: { i: number; value: number }[],
  yAt: (v: number) => number,
  xAt: (i: number) => number,
  floorY: number,
): { line: string; area: string } | null {
  if (samples.length === 0) return null;
  const first = samples[0]!;
  const last = samples[samples.length - 1]!;
  let line = `M ${xAt(first.i)} ${yAt(first.value)}`;
  for (const s of samples.slice(1)) {
    line += ` L ${xAt(s.i)} ${yAt(s.value)}`;
  }
  const area = `${line} L ${xAt(last.i)} ${floorY} L ${xAt(first.i)} ${floorY} Z`;
  return { line, area };
}

function SignalSvg({
  series,
  reportingTz,
}: {
  series: WifiSignalSeries;
  reportingTz: string;
}) {
  const plotW = WIDTH - PAD.left - PAD.right;
  const plotH = CHART_H - PAD.top - PAD.bottom;
  const floorY = PAD.top + plotH;
  const points = series.points;
  const n = points.length;

  const rssiY = (rssi: number) => {
    const t = (rssi - RSSI_MIN) / (RSSI_MAX - RSSI_MIN);
    return PAD.top + plotH * (1 - Math.max(0, Math.min(1, t)));
  };
  const txY = (tx: number) => {
    const t = tx / TX_SCALE_MAX;
    return PAD.top + plotH * (1 - Math.max(0, Math.min(1, t)));
  };
  const xAt = (i: number) => {
    if (n <= 1) return PAD.left + plotW / 2;
    return PAD.left + (i / (n - 1)) * plotW;
  };

  const grainLabel =
    series.grain === "minute" ? "per minute" : series.grain === "hour" ? "hourly avg" : "daily avg";

  if (n === 0) {
    return <p className="text-xs text-text-secondary">No Wi-Fi samples for this table in the window.</p>;
  }

  const rssiSamples = points
    .map((p, i) => ({ i, value: p.rssiDbm }))
    .filter((p): p is { i: number; value: number } => p.value != null && Number.isFinite(p.value));
  const txSamples = points
    .map((p, i) => ({ i, value: p.txMbps }))
    .filter((p): p is { i: number; value: number } => p.value != null && Number.isFinite(p.value));

  const rssiPaths = seriesPaths(rssiSamples, rssiY, xAt, floorY);
  const txPaths = seriesPaths(txSamples, txY, xAt, floorY);

  const labelEvery = Math.max(1, Math.ceil(n / 6));

  return (
    <div className="min-w-0">
      <div className="mb-1 flex flex-wrap items-center gap-3 text-[11px] text-text-secondary">
        <span className="inline-flex items-center gap-1.5">
          <span
            className="inline-block h-0.5 w-4 rounded-sm"
            style={{ background: RSSI_COLOR, boxShadow: `0 0 0 3px ${RSSI_COLOR}44` }}
          />
          Signal (RSSI)
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span
            className="inline-block h-0.5 w-4 rounded-sm"
            style={{ background: TX_COLOR, boxShadow: `0 0 0 3px ${TX_COLOR}44` }}
          />
          Network Speed (TX)
        </span>
        <span className="tabular-nums">
          · dashed = bad threshold · {grainLabel} · {n} point(s)
        </span>
      </div>
      <svg
        viewBox={`0 0 ${WIDTH} ${CHART_H}`}
        className="h-auto w-full max-w-full"
        role="img"
        aria-label={`Signal (RSSI) and Network Speed (TX) over time (${grainLabel}); bad signal ≤ −80 dBm / bad network speed < ${TX_BAD_BELOW} Mbps`}
      >
        <line
          x1={PAD.left}
          y1={floorY}
          x2={PAD.left + plotW}
          y2={floorY}
          stroke="currentColor"
          className="text-border"
          strokeWidth={1}
        />
        {rssiPaths ? (
          <>
            <path d={rssiPaths.area} fill={RSSI_COLOR} opacity={FILL_OPACITY} />
            <path
              d={rssiPaths.line}
              fill="none"
              stroke={RSSI_COLOR}
              strokeWidth={LINE_WIDTH}
              strokeLinejoin="round"
              strokeLinecap="round"
            />
          </>
        ) : null}
        {txPaths ? (
          <>
            <path d={txPaths.area} fill={TX_COLOR} opacity={FILL_OPACITY} />
            <path
              d={txPaths.line}
              fill="none"
              stroke={TX_COLOR}
              strokeWidth={LINE_WIDTH}
              strokeLinejoin="round"
              strokeLinecap="round"
            />
          </>
        ) : null}
        {/* Hover targets */}
        {points.map((p, i) => (
          <circle key={`h-${p.at}`} cx={xAt(i)} cy={floorY} r={8} fill="transparent">
            <title>
              {formatEventTimeLocal(p.at, reportingTz)}
              {p.rssiDbm != null ? `: Signal ${p.rssiDbm.toFixed(0)} dBm` : ""}
              {p.txMbps != null ? `, Network Speed ${p.txMbps.toFixed(1)} Mbps` : ""}
            </title>
          </circle>
        ))}
        {/* Axis spines */}
        <line
          x1={PAD.left}
          y1={PAD.top}
          x2={PAD.left}
          y2={floorY}
          stroke="currentColor"
          className="text-border"
          strokeWidth={1}
        />
        <line
          x1={PAD.left + plotW}
          y1={PAD.top}
          x2={PAD.left + plotW}
          y2={floorY}
          stroke="currentColor"
          className="text-border"
          strokeWidth={1}
        />
        {RSSI_TICKS.map((v) => {
          const y = rssiY(v);
          return (
            <g key={`rssi-${v}`}>
              <line
                x1={PAD.left - 4}
                y1={y}
                x2={PAD.left}
                y2={y}
                stroke="currentColor"
                className="text-border"
                strokeWidth={1}
              />
              <text
                x={PAD.left - 6}
                y={y + 3}
                textAnchor="end"
                className="fill-text-secondary text-[9px] tabular-nums"
              >
                {v}
              </text>
            </g>
          );
        })}
        {TX_TICKS.map((v) => {
          const y = txY(v);
          return (
            <g key={`tx-${v}`}>
              <line
                x1={PAD.left + plotW}
                y1={y}
                x2={PAD.left + plotW + 4}
                y2={y}
                stroke="currentColor"
                className="text-border"
                strokeWidth={1}
              />
              <text
                x={PAD.left + plotW + 6}
                y={y + 3}
                textAnchor="start"
                className="fill-text-secondary text-[9px] tabular-nums"
              >
                {v}
              </text>
            </g>
          );
        })}
        <text
          x={12}
          y={PAD.top + plotH / 2}
          textAnchor="middle"
          transform={`rotate(-90 12 ${PAD.top + plotH / 2})`}
          className="fill-text-secondary text-[10px] font-medium"
        >
          Signal (RSSI)
        </text>
        <text
          x={WIDTH - 10}
          y={PAD.top + plotH / 2}
          textAnchor="middle"
          transform={`rotate(90 ${WIDTH - 10} ${PAD.top + plotH / 2})`}
          className="fill-text-secondary text-[10px] font-medium"
        >
          Network Speed (TX)
        </text>
        <ThresholdLine
          y={rssiY(RSSI_BAD_BELOW)}
          label={`Bad Signal ≤${RSSI_BAD_BELOW} dBm`}
          labelSide="left"
          color={RSSI_COLOR}
        />
        <ThresholdLine
          y={txY(TX_BAD_BELOW)}
          label={`Bad Network Speed <${TX_BAD_BELOW} Mbps`}
          labelSide="right"
          color={TX_COLOR}
        />
        {points.map((p, i) => {
          if (i % labelEvery !== 0 && i !== n - 1) return null;
          return (
            <text
              key={`t-${p.at}`}
              x={xAt(i)}
              y={CHART_H - 6}
              textAnchor="middle"
              className="fill-text-secondary text-[8px]"
            >
              {formatEventTimeLocal(p.at, reportingTz).replace(/^\d{4}-\d{2}-\d{2}\s*/, "")}
            </text>
          );
        })}
      </svg>
    </div>
  );
}

interface WifiSignalOverTimeChartProps {
  filters: FilterState;
  deviceId: string;
  /** Kept for callers; colour is fixed per series, not by table name. */
  deviceLabel: string;
}

/** Signal (RSSI) + Network Speed (TX) area/line series; fixed scales and matching threshold colours. */
export function WifiSignalOverTimeChart({ filters, deviceId }: WifiSignalOverTimeChartProps) {
  const reportingTz = useReportingTimezone();
  const [series, setSeries] = useState<WifiSignalSeries | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    setSeries(null);
    api
      .healthWifiSignal(filters, deviceId)
      .then((next) => {
        if (!cancelled) setSeries(next);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setSeries(null);
          setError(err instanceof Error ? err.message : "Failed to load signal series");
        }
      });
    return () => {
      cancelled = true;
    };
  }, [filters, deviceId]);

  return (
    <div className="mb-3 border-b border-border pb-3">
      <div className="mb-1.5 text-[12px] font-semibold text-text-primary">
        Signal &amp; Network Speed over time
      </div>
      {error ? <p className="text-xs text-err">{error}</p> : null}
      {!error && !series ? <p className="text-xs text-text-secondary">Loading signal…</p> : null}
      {!error && series && !series.available ? (
        <p className="text-xs text-text-secondary">
          Wi-Fi history is not in this database yet (`device_telemetry_minutes`).
        </p>
      ) : null}
      {!error && series?.available ? (
        <SignalSvg series={series} reportingTz={reportingTz} />
      ) : null}
    </div>
  );
}
